import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { renderColoredText } from '../utils/colorParser';
import './BoostPage.css';

function BoostPage({ authToken, onTokenExpiration }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [serverData, setServerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(1);
  const [months, setMonths] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const basePrice = 30000; // Base price per boost per month in tomans

  const fetchServerData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`https://api.vmp.ir/server/api.php?work=singleData2&id=${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.Data) {
        setServerData(data);
      } else {
        throw new Error('Invalid server data received');
      }
    } catch (error) {
      console.error('Failed to fetch server data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchServerData();
    }
  }, [id]);

  const handleBackToDashboard = () => {
    navigate('/');
  };

  // Pricing calculations
  const calculatePricing = () => {
    const discountRateCalc = (0.008 * months) + (0.0014 * count);
    const finalPrice = basePrice * months * count;
    const finalPriceDis = Math.round(finalPrice * (1 - discountRateCalc));

    return {
      discountRate: Math.round(discountRateCalc * 100 * 10) / 10, // Round to 1 decimal
      finalPrice,
      finalPriceDis,
      savings: finalPrice - finalPriceDis
    };
  };

  const pricing = calculatePricing();

  const handlePurchaseBoost = async () => {
    try {
      setPurchasing(true);
      
      const safeCount = Math.min(100, Math.max(1, parseInt(count) || 1));
      const safeMonths = Math.min(12, Math.max(1, parseInt(months) || 1));
      const response = await fetch(`https://api.vmp.ir/dev/api.php?work=buyBoost&serverId=${serverData?.Data?.id}&boost=${safeCount}&month=${safeMonths}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.text();
      
      if (result && result.trim()) {
        let redirectUrl;
        try {
          redirectUrl = new URL(result.trim());
        } catch (e) {
          throw new Error('Invalid redirect URL from server');
        }
        if (redirectUrl.protocol === 'https:') {
          window.location.href = redirectUrl.toString();
        } else {
          throw new Error('Untrusted redirect URL from server');
        }
      } else {
        throw new Error('No redirect URL received from server');
      }
    } catch (error) {
      console.error('Failed to purchase boost:', error);
      alert('Failed to process purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="boost-page">
        <div className="boost-container">
          <button onClick={handleBackToDashboard} className="back-button">
            ← Back to Dashboard
          </button>
          <div className="boost-header">
            <h2>Loading Server...</h2>
            <p>Please wait while we fetch server information.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="boost-page">
        <div className="boost-container">
          <button onClick={handleBackToDashboard} className="back-button">
            ← Back to Dashboard
          </button>
          <div className="boost-header">
            <h2>Error Loading Server</h2>
            <p>Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="boost-page">
      <div className="boost-container">
        <button onClick={handleBackToDashboard} className="back-button">
          ← Back to Dashboard
        </button>
        
        <div className="server-info-card">
          <div className="server-details">
            <div className="server-header">
              <div className="server-image-container">
                <img 
                  src={`https://cdn.vmp.ir/saturn/icon/${id}-1.png`}
                  alt="Server Icon"
                  className="server-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h3>Server Information</h3>
            </div>
            {serverData?.Data?.vars?.sv_projectName && (
              <div className="server-field">
                <span className="field-label">Server Name:</span>
                <span className="field-value">
                  {renderColoredText(serverData.Data.vars.sv_projectName)}
                </span>
              </div>
            )}
            <div className="server-field">
              <span className="field-label">Server ID:</span>
              <span className="field-value">{id}</span>
            </div>
            {serverData?.Data?.upvotePower !== undefined && (
              <div className="current-boosts-field">
                <span className="current-boosts-label">Current Boosts</span>
                <span className="current-boosts-value">{serverData.Data.upvotePower}</span>
              </div>
            )}
          </div>
        </div>

        <div className="boost-calculator-card">
          <div className="calculator-content">
            <h3>Boost Calculator</h3>
            
            <div className="slider-section">
              <div className="slider-group">
                <label className="slider-label">
                  Count: <span className="slider-value">{count}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value))}
                  className="slider"
                />
                <div className="slider-range">1 - 100</div>
              </div>

              <div className="slider-group">
                <label className="slider-label">
                  Months: <span className="slider-value">{months}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="12"
                  value={months}
                  onChange={(e) => setMonths(parseInt(e.target.value))}
                  className="slider"
                />
                <div className="slider-range">1 - 12</div>
              </div>
            </div>

            <div className="pricing-section">
              <div className="price-display">
                <div className="original-price">
                  <span className="original-price-label">Original Price</span>
                  <span className="original-price-value">{pricing.finalPrice.toLocaleString()} T</span>
                </div>
                
                <div className="discount-badge">
                  <span className="discount-percentage">-{pricing.discountRate}%</span>
                  <span className="savings-text">You save {pricing.savings.toLocaleString()} T</span>
                </div>
                
                <div className="final-price">
                  <span className="final-price-label">Final Price</span>
                  <span className="final-price-value">{pricing.finalPriceDis.toLocaleString()} T</span>
                </div>
              </div>
              
              <button 
                className="purchase-button"
                onClick={handlePurchaseBoost}
                disabled={purchasing}
              >
                {purchasing ? 'Processing...' : 'Purchase Boost'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BoostPage;